import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, inject, model, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { FluidModule } from 'primeng/fluid';
import { AutoCompleteMode, AutoCompleteOptions } from '../../../shared/components/phx-input-auto-complete/phx-input-auto-complete-interfaces-types';
import { AutoCompleteService } from '../../../shared/components/phx-input-auto-complete/phx-input-auto-complete.service';

@Component({
  selector: 'app-formly-input-chip',
  templateUrl: './formly-input-chip.component.html',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AutoComplete,
    FluidModule
  ],
  providers: [AutoCompleteService]
})
export class FormlyInputAutoCompleteComponent extends FieldType<FieldTypeConfig> implements AfterViewInit {
  private destroyRef = inject(DestroyRef);
  private autoCompleteService = inject(AutoCompleteService);

  values = model<string[]>([]);
  typeahead = model(true);
  invalidEmailMessage = model<string | null>(null);
  isFadingOut = model<boolean>(false);
  suggestions = model<string[]>([]);
  filteredSuggestions = signal<string[]>(null, { equal: () => false });
  separatorRegex = model(/[,;]/);
  defaultSeparator = signal<string>(',');

  constructor() {
    super();
    this.destroyRef.onDestroy(() => {
      console.log(`Destroying ${this.constructor.name} component`);
    });
    this.values.subscribe(value => {
      // Recipient MUST be a string separated by comma/semicolon.
      // This needs improvement in Backend to be as a normal array.
      if (Array.isArray(value)) {
        // Convert array to a comma-separated string
        const formattedValue = value.join(this.defaultSeparator());

        // Update the form control with the formatted string
        this.form.get(this.key as string).patchValue(formattedValue);
        this.form.get(this.key as string).updateValueAndValidity();
      } else {
        // Update the form control with the original value
        this.form.get(this.key as string).patchValue(value);
        this.form.get(this.key as string).updateValueAndValidity();
      }
    });
  }

  ngAfterViewInit() {
    if (this.props.suggestions) {
      this.suggestions.update(() => {
        const { values } = this.autoCompleteService.processValues(
          this.props.suggestions,
          AutoCompleteMode.EMAIL,
          this.getProcessingOptions()
        );
        return values;
      });
    }

    const initialValues = this.form.get(this.key as string).value;
    if (!initialValues) return;

    this.values.update(() => {
      const { values } = this.autoCompleteService.processValues(
        initialValues,
        AutoCompleteMode.EMAIL,
        this.getProcessingOptions()
      );
      return values;
    });
  }

  onSelect(event: AutoCompleteSelectEvent) {
    this.addValues(event.value);
  }

  onEnter(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    if (this.addValues(inputElement.value)) {
      inputElement.value = '';
    }
  }

  addValues(input: string): boolean {
    const trimmedInput = input.trim();
    if (!trimmedInput) {
      return false;
    }

    const { values: processedValues, isInvalid } = this.autoCompleteService.processValues(
      trimmedInput,
      AutoCompleteMode.EMAIL,
      this.getProcessingOptions()
    );

    if (isInvalid) {
      this.autoCompleteService.setTemporaryError(
        this.invalidEmailMessage.set,
        this.isFadingOut.set,
        'common.invalid-emails-detected'
      );
      return false;
    }

    if (processedValues.length === 0) {
      return false;
    }

    this.values.update((values) => Array.from(new Set([...values, ...processedValues])));
    return true;
  }

  onBlur(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const raw = inputElement.value.trim();
    if (!raw) {
      return;
    }
    if (this.addValues(raw)) {
      inputElement.value = '';
    }
  }

  search(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();
    this.filteredSuggestions.set(
      this.autoCompleteService.filterSuggestions(
        this.suggestions(),
        query,
        this.values()
      )
    );
  }

  private getProcessingOptions(): AutoCompleteOptions {
    return {
      separatorRegex: this.separatorRegex(),
      trimValues: true,
      removeDuplicates: true,
      validationEnabled: true
    };
  }
}
